import React, { useEffect, useState } from 'react';
import { Sidebar } from '../../../_shared/Sidebar';
import { Checkbox } from '../../../booking-bar/src/components/Checkbox';
import { MinusPlus } from '../../../booking-bar/src/components/MinusPlus';
import { ReactComponent as ArrowDownIcon } from '../icons/arrow-down.svg';
import { ReactComponent as PlusIcon } from '../../../booking-bar/src/icons/plus-bold.svg';
import { dispatchOceanSearch, getSearchParamsFromUrl, OCEAN_OPEN_FILTERS_EVENT, OCEAN_SEARCH_EVENT, OceanSearchParams } from '../../../_shared/oceanSearchContext';
import { useDebouncedCallback } from '../../../_shared/useDebouncedCallback';

interface BookingFiltersProps
	{
	// Every property-feature term (unfiltered -- booking-filters shows them
	// all, unlike booking-bar's search-bar-only subset). `pinned` terms
	// render as their own checkboxes above the collapsible list instead of
	// inside it.
	features: Array<{ value: string; label: string; icon?: string | null; caveat?: string | null; pinned?: boolean }>;
	locations: Array<{ value: string; label: string }>;
	collections: Array<{ value: string; label: string }>;
	showTrigger?: boolean;
	}

interface FilterSectionProps
	{
	title: string;
	isOpen: boolean;
	onToggle: () => void;
	// How many of this section's own options are currently selected --
	// drives the right-aligned indicator: an "N selected" count while
	// collapsed, or a "Clear" action (onClear) once expanded. Omitted
	// (0/undefined) sections -- e.g. Collections, which has its own "Any"
	// option as its clear affordance -- show neither.
	count?: number;
	onClear?: () => void;
	children: React.ReactNode;
	}

const FilterSection = ({ title, isOpen, onToggle, count = 0, onClear, children }: FilterSectionProps) =>
	{
	return (
		<div className="booking-filters__section">
			<div className="booking-filters__section-toggle">
				<button type="button" className="booking-filters__section-title" onClick={onToggle} aria-expanded={isOpen}>
					{title}
				</button>
				{count > 0 && (
					isOpen ? (
						<button type="button" className="booking-filters__section-clear" onClick={onClear}>
							Clear
						</button>
					) : (
						<span className="booking-filters__section-count">{count} selected</span>
					)
				)}
				<button
					type="button"
					className="booking-filters__section-arrow-btn"
					onClick={onToggle}
					aria-expanded={isOpen}
					aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${title}`}
				>
					<ArrowDownIcon className={`booking-filters__section-arrow ${isOpen ? 'is-open' : ''}`} />
				</button>
			</div>
			<div className={`booking-filters__section-body ocean-collapsible ${isOpen ? 'is-open' : ''}`}>
				<div className="booking-filters__section-inner">
					{children}
				</div>
			</div>
		</div>
		);
	};

export const BookingFilters = ({ features, locations, collections, showTrigger = true }: BookingFiltersProps) =>
	{
	const [isOpen, setIsOpen] = useState(false);
	const [featuresOpen, setFeaturesOpen] = useState(false);
	const [destinationsOpen, setDestinationsOpen] = useState(false);
	const [collectionsOpen, setCollectionsOpen] = useState(false);
	const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
	const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
	const [selectedCollection, setSelectedCollection] = useState('');
	const [minBedrooms, setMinBedrooms] = useState(0);
	const [minBathrooms, setMinBathrooms] = useState(0);

	const pinnedFeatures = features.filter((f) => f.pinned);
	const regularFeatures = features.filter((f) => !f.pinned);

	const openSidebar = () => {
		// Seed the draft from whatever's currently active, so reopening
		// shows the filters as they were last applied.
		const current = getSearchParamsFromUrl();
		setSelectedFeatures(current.amenities ? current.amenities.split(',').filter(Boolean) : []);
		setSelectedLocations(current.location ? current.location.split(',').filter(Boolean) : []);
		setSelectedCollection(current.collection || '');
		setMinBedrooms(parseInt(current.min_bedrooms || '0', 10) || 0);
		setMinBathrooms(parseInt(current.min_bathrooms || '0', 10) || 0);
		setIsOpen(true);
		};

	// ocean/property-results' "Filter" link opens this sidebar remotely --
	// see dispatchOpenFilters() in oceanSearchContext.ts.
	useEffect(() => {
		document.addEventListener(OCEAN_OPEN_FILTERS_EVENT, openSidebar);
		return () => document.removeEventListener(OCEAN_OPEN_FILTERS_EVENT, openSidebar);
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

	// Another block on the page (e.g. an ocean/collections card) can change
	// the collection filter directly rather than through this sidebar --
	// keep the Collections radio in step if that happens while it's open, so
	// it doesn't show a stale selection on next open.
	useEffect(() => {
		const handleSearchEvent = (event: Event) => {
			const detail = (event as CustomEvent<Partial<OceanSearchParams>>).detail || {};
			if (detail.collection !== undefined)
				{
				setSelectedCollection(detail.collection);
				}
			};
		document.addEventListener(OCEAN_SEARCH_EVENT, handleSearchEvent);
		return () => document.removeEventListener(OCEAN_SEARCH_EVENT, handleSearchEvent);
		}, []);

	// Every field dispatches live -- `overrides` lets the field that just
	// changed supply its new value immediately rather than waiting a render
	// for its own state to update. Debounced (see dispatchLive below) so
	// several quick changes (ticking a run of amenity checkboxes) collapse
	// into one request instead of firing per click.
	const buildParams = (overrides: Partial<OceanSearchParams> = {}): Partial<OceanSearchParams> => ({
		...getSearchParamsFromUrl(),
		amenities: selectedFeatures.join(','),
		location: selectedLocations.join(','),
		collection: selectedCollection,
		min_bedrooms: minBedrooms > 0 ? minBedrooms.toString() : '',
		min_bathrooms: minBathrooms > 0 ? minBathrooms.toString() : '',
		...overrides,
		});

	const dispatchLive = useDebouncedCallback((params: Partial<OceanSearchParams>) => dispatchOceanSearch(params), 400);

	const handleFeatureToggle = (value: string) => {
		const next = selectedFeatures.includes(value) ? selectedFeatures.filter((f) => f !== value) : [...selectedFeatures, value];
		setSelectedFeatures(next);
		dispatchLive(buildParams({ amenities: next.join(',') }));
		};

	const handleLocationToggle = (value: string) => {
		const next = selectedLocations.includes(value) ? selectedLocations.filter((l) => l !== value) : [...selectedLocations, value];
		setSelectedLocations(next);
		dispatchLive(buildParams({ location: next.join(',') }));
		};

	const handleCollectionChange = (value: string) => {
		setSelectedCollection(value);
		dispatchLive(buildParams({ collection: value }));
		};

	const handleMinBedroomsChange = (value: number) => {
		setMinBedrooms(value);
		dispatchLive(buildParams({ min_bedrooms: value > 0 ? value.toString() : '' }));
		};

	const handleMinBathroomsChange = (value: number) => {
		setMinBathrooms(value);
		dispatchLive(buildParams({ min_bathrooms: value > 0 ? value.toString() : '' }));
		};

	// Per-section "Clear" -- immediate (non-debounced), and scoped to just
	// that section's own options rather than every filter at once (a single
	// blanket "Clear filters" was ambiguous about what it actually reset).
	const handleClearFeatures = () => {
		// Only the regular (collapsible-list) features -- pinned ones sit
		// outside this section and keep their own selections untouched.
		const regularValues = new Set(regularFeatures.map((f) => f.value));
		const next = selectedFeatures.filter((v) => !regularValues.has(v));
		setSelectedFeatures(next);
		dispatchOceanSearch(buildParams({ amenities: next.join(',') }));
		};

	const handleClearLocations = () => {
		setSelectedLocations([]);
		dispatchOceanSearch(buildParams({ location: '' }));
		};

	return (
		<div className="booking-filters">
			{showTrigger && (
				<button type="button" className="booking-filters__trigger" onClick={openSidebar}>
					<span>Filter and sort</span>
					<PlusIcon />
				</button>
				)}

			<Sidebar isOpen={isOpen} onClose={() => setIsOpen(false)} title="Filters">
				<div className="booking-filters__steppers">
					<MinusPlus value={minBedrooms} onChange={handleMinBedroomsChange} min={0} max={20} label="Min. bedrooms" />
					<MinusPlus value={minBathrooms} onChange={handleMinBathroomsChange} min={0} max={20} label="Min. bathrooms" />
				</div>

				{pinnedFeatures.length > 0 && (
					<div className="booking-filters__checkbox-list booking-filters__pinned-features">
						{pinnedFeatures.map((feature) => (
							<Checkbox
								key={feature.value}
								checked={selectedFeatures.includes(feature.value)}
								onChange={() => handleFeatureToggle(feature.value)}
								label={feature.label}
								icon={feature.icon}
								caveat={feature.caveat}
							/>
						))}
					</div>
				)}

				{regularFeatures.length > 0 && (
					<FilterSection
						title="Property features"
						isOpen={featuresOpen}
						onToggle={() => setFeaturesOpen(!featuresOpen)}
						count={regularFeatures.filter((f) => selectedFeatures.includes(f.value)).length}
						onClear={handleClearFeatures}
					>
						<div className="booking-filters__checkbox-list">
							{regularFeatures.map((feature) => (
								<Checkbox
									key={feature.value}
									checked={selectedFeatures.includes(feature.value)}
									onChange={() => handleFeatureToggle(feature.value)}
									label={feature.label}
									icon={feature.icon}
									caveat={feature.caveat}
								/>
							))}
						</div>
					</FilterSection>
				)}

				{locations.length > 0 && (
					<FilterSection
						title="Destinations"
						isOpen={destinationsOpen}
						onToggle={() => setDestinationsOpen(!destinationsOpen)}
						count={selectedLocations.length}
						onClear={handleClearLocations}
					>
						<div className="booking-filters__checkbox-list">
							{locations.map((location) => (
								<Checkbox
									key={location.value}
									checked={selectedLocations.includes(location.value)}
									onChange={() => handleLocationToggle(location.value)}
									label={location.label}
								/>
							))}
						</div>
					</FilterSection>
				)}

				{collections.length > 0 && (
					<FilterSection title="Collections" isOpen={collectionsOpen} onToggle={() => setCollectionsOpen(!collectionsOpen)}>
						<ul className="booking-filters__radio-list">
							<li>
								<label className="booking-filters__radio-option">
									<input
										type="radio"
										name="booking-filters-collection"
										checked={selectedCollection === ''}
										onChange={() => handleCollectionChange('')}
									/>
									Any
								</label>
							</li>
							{collections.map((collection) => (
								<li key={collection.value}>
									<label className="booking-filters__radio-option">
										<input
											type="radio"
											name="booking-filters-collection"
											checked={selectedCollection === collection.value}
											onChange={() => handleCollectionChange(collection.value)}
										/>
										{collection.label}
									</label>
								</li>
							))}
						</ul>
					</FilterSection>
				)}
			</Sidebar>
		</div>
		);
	};
